import type { TailleDeVignettes } from '@taki/ui';

/**
 * LA LOGIQUE PURE DE LA CARTE DES FICHIERS EN VIGNETTES, sans React ni DOM :
 * `FilesPanel.tsx` et `VisionneuseDImages.tsx` la montent, `tests/vignettes.test.mjs` la prouve.
 */

/** Comment la carte montre ses fichiers : la liste d'avant, ou une grille de vignettes. */
export type AffichageDesFichiers = 'liste' | 'vignettes';

/**
 * LA CARTE S'OUVRE TOUJOURS EN VIGNETTES (recette du 02/11 : « c'est juste la
 * taille qu'on veut ») ; son reglage n'en donne que la taille, et la bascule
 * passe en liste, sans rien retenir.
 */
export const AFFICHAGE_D_OUVERTURE: AffichageDesFichiers = 'vignettes';

/**
 * LA LARGEUR MINIMALE D'UNE VIGNETTE, par taille. Les trois sont nettement
 * differentes : un ecart de deux rem ne se verrait pas sur la grille.
 */
export const LARGEUR_DE_VIGNETTE: Readonly<Record<TailleDeVignettes, string>> = {
  petites: '6rem',
  moyennes: '9rem',
  grandes: '13rem',
};

/** La taille des vignettes d'une carte sans reglage : la clef absente. */
export const TAILLE_PAR_DEFAUT: TailleDeVignettes = 'moyennes';

/** La largeur minimale d'une vignette ; une taille absente ou inconnue vaut les moyennes. */
export function largeurDeVignette(taille: TailleDeVignettes | undefined): string {
  return LARGEUR_DE_VIGNETTE[taille ?? TAILLE_PAR_DEFAUT] ?? LARGEUR_DE_VIGNETTE[TAILLE_PAR_DEFAUT];
}

/** La valeur de `grid-template-columns` de la grille : autant de colonnes que la largeur en laisse. */
export function colonnesDeVignettes(taille: TailleDeVignettes | undefined): string {
  return `repeat(auto-fill, minmax(${largeurDeVignette(taille)}, 1fr))`;
}

/** La taille des vignettes montrees : le reglage `carte.vignettes`, ou les moyennes. */
export function tailleAffichee(vignettes: TailleDeVignettes | undefined): TailleDeVignettes {
  return vignettes ?? TAILLE_PAR_DEFAUT;
}

/** Ce qui designe un affichage devient un affichage ; tout ce qui n'est pas « vignettes » est la liste. */
function affichageNomme(valeur: unknown): AffichageDesFichiers {
  return valeur === 'vignettes' ? 'vignettes' : 'liste';
}

/** L'identifiant que le selecteur de vues du socle (`ViewSelector`) donne a un affichage. */
export function idDeVue(affichage: AffichageDesFichiers): `fichiers.${AffichageDesFichiers}` {
  return `fichiers.${affichage}`;
}

/** L'affichage que designe un identifiant du selecteur ; un identifiant inconnu est la liste. */
export function affichageDeVue(id: string): AffichageDesFichiers {
  return affichageNomme(id.split('.')[1]);
}

/** Le fichier est-il une image que le navigateur sait montrer ? */
export function estUneImage(mimeType: string): boolean {
  return mimeType.toLowerCase().startsWith('image/');
}

/**
 * L'image peut-elle s'animer (GIF, WebP, APNG) ? Sa vignette est alors figee sur
 * sa premiere image, et ne s'anime qu'au survol ou dans la visionneuse.
 */
export function estAnimable(mimeType: string): boolean {
  return ['image/gif', 'image/webp', 'image/apng'].includes(mimeType.trim().toLowerCase());
}

/** L'extension d'un nom, en majuscules et courte, pour la vignette type : « PDF », « DOCX ». Vide sans extension. */
export function extensionAffichee(nom: string): string {
  const point = nom.lastIndexOf('.');
  if (point <= 0 || point === nom.length - 1) return '';
  return nom.slice(point + 1).toUpperCase().slice(0, 5);
}

/**
 * LE FORMAT D'UN FICHIER, en deux mots, pour la ligne de la liste (recette du
 * 02/11 : « image GIF, la taille, et c'est tout ») : « Image GIF », « Vidéo MP4 »,
 * « PDF », sinon l'extension (« DOCX »), sinon « Fichier ».
 */
export function formatAffiche(mimeType: string, nom: string): string {
  const [famille = '', sorte = ''] = mimeType.trim().toLowerCase().split('/');
  const extension = extensionAffichee(nom);
  const precis = (sorte.split('+')[0] ?? '').replace(/^x-/, '').toUpperCase() || extension;
  if (famille === 'image') return precis === '' ? 'Image' : `Image ${precis}`;
  if (famille === 'video') return precis === '' ? 'Vidéo' : `Vidéo ${precis}`;
  if (famille === 'audio') return precis === '' ? 'Audio' : `Audio ${precis}`;
  if (sorte === 'pdf') return 'PDF';
  return extension === '' ? 'Fichier' : extension;
}

/** La position d'une image parmi les images de la liste (de 0), ou -1. */
export function rangDImage(images: readonly { id: string }[], id: string): number {
  return images.findIndex((image) => image.id === id);
}

/**
 * L'image d'a cote, BORNEE : `null` au bout de la liste (le bouton se grise),
 * jamais de retour a l'autre bout. `sens` : -1 precedent, 1 suivant.
 */
export function imageVoisine(images: readonly { id: string }[], id: string, sens: -1 | 1): string | null {
  const rang = rangDImage(images, id);
  if (rang < 0) return null;
  return images[rang + sens]?.id ?? null;
}

/**
 * UNE FILE QUI LIMITE LES LECTURES SIMULTANEES : une grille de cinquante
 * images ne lance pas cinquante lectures d'un coup. Chaque tache part des
 * qu'une place se libere, dans l'ordre ou elles ont ete demandees ; une
 * tache qui echoue libere sa place comme les autres.
 */
export function limiteurDeLectures(maximum: number): <T>(tache: () => Promise<T>) => Promise<T> {
  let actives = 0;
  const attente: Array<() => void> = [];
  return async <T,>(tache: () => Promise<T>): Promise<T> => {
    /* La place est PASSEE de main en main : quand une tache finit et qu'une autre
       attend, `actives` ne bouge pas, personne ne peut la prendre entre les deux. */
    if (actives >= maximum) await new Promise<void>((libre) => attente.push(libre));
    else actives += 1;
    try {
      return await tache();
    } finally {
      const suivante = attente.shift();
      if (suivante !== undefined) suivante();
      else actives -= 1;
    }
  };
}

/** Le nombre de lectures d'images simultanees. */
export const LECTURES_SIMULTANEES = 4;
